import React, { useCallback, memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Row, Col, Select, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import FilterWrapper from './wrapper';
import { Button } from 'components';
import { DeleteIcon } from 'icons';

const { Option } = Select;

const WebsiteFilters = memo(
  ({
    shoppingCenters = [],
    companies = [],
    categories = [],
    onFiltersChange,
    loading = false,
    additionalFilters = [],
    showInsegna = true,
    showCategorie = true,
    enableSorting = false,
    filters = {},
    showSearch = true,
  }) => {
    const { t } = useTranslation();
    // Derived lists are computed with useMemo to avoid setState loops
    const [selectedVillage, setSelectedVillage] = useState(
      filters.shopping_center_id || null,
    );
    const [searchValue, setSearchValue] = useState(filters.by_title || '');

    const debouncedSearch = useCallback(
      debounce((searchTerm) => {
        const newFilters = { by_title: searchTerm };
        onFiltersChange(newFilters);
      }, 1500),
      [onFiltersChange],
    );

    const handleSearchChange = useCallback(
      (e) => {
        const value = e.target.value;
        setSearchValue(value);
        debouncedSearch(value);
      },
      [debouncedSearch],
    );

    const handleFilterChange = useCallback(
      (filterName, value) => {
        const newFilters = {};

        if (filterName === 'shopping_center_id') {
          newFilters.shopping_center_id = value || null;
          setSelectedVillage(value || null);
        } else if (filterName === 'company_id') {
          newFilters.company_id = value || null;
        } else if (filterName === 'category_id') {
          newFilters.category_id = value || null;
        }

        console.log('Filter change:', filterName, value, newFilters);
        onFiltersChange(newFilters);
      },
      [onFiltersChange],
    );

    const handleClearFilters = useCallback(() => {
      setSearchValue('');
      setSelectedVillage(null);
      // Скидаємо всі фільтри, передаючи null для кожного
      onFiltersChange({
        shopping_center_id: null,
        company_id: null,
        category_id: null,
        by_title: null,
      });
    }, [onFiltersChange]);

    const filteredCompanies = useMemo(() => {
      if (enableSorting && selectedVillage) {
        const villageTitle =
          shoppingCenters.find(
            (center) => (center.value || center.id) === selectedVillage,
          )?.title || selectedVillage;
        return companies.filter(
          (company) =>
            company.title?.includes(villageTitle) ||
            company.shopping_center_title?.includes(villageTitle),
        );
      }
      return companies;
    }, [companies, enableSorting, selectedVillage, shoppingCenters]);

    const filteredCategories = useMemo(() => {
      if (enableSorting && selectedVillage) {
        const villageTitle =
          shoppingCenters.find(
            (center) => (center.value || center.id) === selectedVillage,
          )?.title || selectedVillage;
        return categories.filter(
          (category) =>
            category.title?.includes(villageTitle) ||
            category.shopping_center_title?.includes(villageTitle),
        );
      }
      return categories;
    }, [categories, enableSorting, selectedVillage, shoppingCenters]);

    useEffect(() => {
      // Sync only when values actually change to avoid loops
      const nextVillage = filters.shopping_center_id || null;
      const nextSearch = filters.by_title || '';
      setSelectedVillage((prev) => (prev === nextVillage ? prev : nextVillage));
      setSearchValue((prev) => (prev === nextSearch ? prev : nextSearch));
    }, [filters.shopping_center_id, filters.by_title]);

    // Функції для отримання title за ID
    const getVillageTitle = useCallback(
      (id) => {
        if (!id) return null;
        const village = shoppingCenters.find((center) => {
          const centerId = center.value || center.id;
          return centerId == id; // Використовуємо == для порівняння рядка з числом
        });
        return village ? village.title || village.name : null;
      },
      [shoppingCenters],
    );

    const getCompanyTitle = useCallback(
      (id) => {
        if (!id) return null;
        const company = companies.find((comp) => {
          const compId = comp.value || comp.id;
          return compId == id; // Використовуємо == для порівняння рядка з числом
        });
        return company ? company.title || company.name : null;
      },
      [companies],
    );

    const getCategoryTitle = useCallback(
      (id) => {
        if (!id) return null;
        const category = categories.find((cat) => {
          const catId = cat.value || cat.id;
          return catId == id; // Використовуємо == для порівняння рядка з числом
        });
        return category ? category.title || category.name : null;
      },
      [categories],
    );

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <FilterWrapper title={t('Outlet Village')}>
            <Select
              bordered={false}
              placeholder={t('Outlet Village')}
              style={{ minWidth: 120 }}
              dropdownMatchSelectWidth={false}
              value={
                selectedVillage
                  ? {
                      value: selectedVillage,
                      label: getVillageTitle(selectedVillage),
                    }
                  : undefined
              }
              onChange={(value) =>
                handleFilterChange('shopping_center_id', value?.value)
              }
              allowClear
              showSearch
              labelInValue
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {shoppingCenters
                .filter(
                  (center) =>
                    center &&
                    (center.value || center.id) &&
                    (center.title || center.name),
                )
                .map((center) => (
                  <Option
                    key={center.value || center.id}
                    value={center.value || center.id}
                    label={center.title || center.name}
                  >
                    {center.title || center.name}
                  </Option>
                ))}
            </Select>
          </FilterWrapper>
        </Col>

        {showInsegna && (
          <Col>
            <FilterWrapper title={t('Insegna')}>
              <Select
                bordered={false}
                placeholder={t('Insegna')}
                style={{ minWidth: 120 }}
                dropdownMatchSelectWidth={false}
                value={
                  filters.company_id
                    ? {
                        value: filters.company_id,
                        label: getCompanyTitle(filters.company_id),
                      }
                    : undefined
                }
                onChange={(value) =>
                  handleFilterChange('company_id', value?.value)
                }
                allowClear
                showSearch
                labelInValue
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {filteredCompanies
                  .filter(
                    (company) =>
                      company &&
                      (company.value || company.id) &&
                      (company.title || company.name),
                  )
                  .map((company) => (
                    <Option
                      key={company.value || company.id}
                      value={company.value || company.id}
                      label={company.title || company.name}
                    >
                      {company.title || company.name}
                    </Option>
                  ))}
              </Select>
            </FilterWrapper>
          </Col>
        )}

        {showCategorie && (
          <Col>
            <FilterWrapper title={t('Categorie')}>
              <Select
                bordered={false}
                placeholder={t('Categorie')}
                style={{ minWidth: 120 }}
                dropdownMatchSelectWidth={false}
                value={
                  filters.category_id
                    ? {
                        value: filters.category_id,
                        label: getCategoryTitle(filters.category_id),
                      }
                    : undefined
                }
                onChange={(value) =>
                  handleFilterChange('category_id', value?.value)
                }
                allowClear
                showSearch
                labelInValue
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {filteredCategories
                  .filter(
                    (category) =>
                      category &&
                      (category.value || category.id) &&
                      (category.title || category.name),
                  )
                  .map((category) => (
                    <Option
                      key={category.value || category.id}
                      value={category.value || category.id}
                      label={category.title || category.name}
                    >
                      {category.title || category.name}
                    </Option>
                  ))}
              </Select>
            </FilterWrapper>
          </Col>
        )}
        {showSearch && (
          <Col>
            <FilterWrapper title={t('Cerca')} hideClose={true}>
              <Input
                placeholder={t('Cerca')}
                bordered={false}
                value={searchValue}
                onChange={handleSearchChange}
                prefix={<SearchOutlined />}
              />
            </FilterWrapper>
          </Col>
        )}

        <Col>
          <Button
            onClick={handleClearFilters}
            iconMargin={true}
            type={'red'}
            icon={<DeleteIcon />}
          >
            {t('Cancella i filtri')}
          </Button>
        </Col>
      </Row>
    );
  },
);

WebsiteFilters.propTypes = {
  shoppingCenters: PropTypes.array,
  companies: PropTypes.array,
  categories: PropTypes.array,
  onFiltersChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  additionalFilters: PropTypes.array,
  showInsegna: PropTypes.bool,
  showCategorie: PropTypes.bool,
  enableSorting: PropTypes.bool,
  filters: PropTypes.object,
};

export default WebsiteFilters;
